import { getAPIPath } from '../browser-extension/utils/api-path-utils';
import { getAuthToken } from '../utils/tokenUtils';
import { isExtension } from '../browser-extension/utils/extension-utils';
import { refreshSession } from '@plerionhq/design-system';
import { isAxiosError } from 'axios';

export class WebSocketService {
  private static instance: WebSocketService;
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectTimeout = 1000; // Initial back-off
  private maxReconnectTimeout = 30000; // Back-off cap
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectPromise: Promise<void> | null = null;
  private intentionalClose = false;
  private sessionExpired = false;
  private refreshUnauthorized = false;
  private url: string;
  private messageHandler: ((message: unknown) => void) | null = null;
  private connectionId: string;
  private chromePort: chrome.runtime.Port | null = null;

  private constructor(url: string) {
    this.url = url;
    this.connectionId = crypto.randomUUID();
    this.registerRecoveryListeners();
  }

  public static getInstance(url: string): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService(url);
    }
    return WebSocketService.instance;
  }

  public async connect(): Promise<void> {
    // Connecting declares intent to stay connected, undoing any prior close
    this.intentionalClose = false;
    this.sessionExpired = false;

    // Wait out any in-flight attempt so concurrent calls share one socket
    while (this.connectPromise) {
      await this.connectPromise;
    }

    if (
      this.ws?.readyState === WebSocket.CONNECTING ||
      this.ws?.readyState === WebSocket.OPEN
    ) {
      return;
    }

    this.connectPromise = this.doConnect().finally(() => {
      this.connectPromise = null;
    });
    return this.connectPromise;
  }

  private async doConnect(): Promise<void> {
    this.clearReconnectTimer();

    try {
      const apiPath = await getAPIPath();
      if (!apiPath) {
        throw new Error('No API path found');
      }
      const connectionUrl = this.getConnectionUrl(apiPath);
      if (!connectionUrl) {
        throw new Error('No connection URL found');
      }

      if (isExtension) {
        const token = await getAuthToken();
        if (this.intentionalClose) {
          return;
        }
        // Use Chrome extension WebSocket adapter
        await this.connectViaChromeExtension(apiPath, token || '');
      } else {
        // $connect authenticates from the accessToken cookie, which is
        // short-lived in production. Refresh it before every handshake.
        await this.refreshSessionBeforeConnect();
        // disconnect() may have been called while we were awaiting
        if (this.intentionalClose) {
          return;
        }

        this.ws = new WebSocket(connectionUrl, ['cookie-access-token']);

        this.setupWebSocketEventHandlers();
      }
    } catch (error) {
      console.error('[WebSocketService] Connection error:', error);
      this.scheduleReconnect();
    }
  }

  private async refreshSessionBeforeConnect(): Promise<void> {
    // Only a 401 from this attempt means the session cannot be refreshed
    this.refreshUnauthorized = false;
    try {
      await refreshSession();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 401) {
        // No refresh token or it expired. Still try the handshake: the
        // current cookie may be valid. If the handshake also fails, the
        // close handler stops retrying for good.
        console.info(
          '[WebSocketService] Session refresh unauthorized, trying existing credentials',
        );
        this.refreshUnauthorized = true;
        return;
      }
      // Transient failure (e.g. network), the current cookie may still work
      console.info('[WebSocketService] Session refresh failed:', error);
    }
  }

  getConnectionUrl(apiPath: string): string {
    if (this.connectToLocal()) {
      if (window.location.search.includes('local-')) {
        return `ws://localhost:3999/wss-api?connectionId=${this.connectionId}`;
      }
      if (apiPath.includes('localhost')) {
        return `ws://${apiPath}/wss-api`;
      }
    }

    return `wss://${apiPath}/wss-api`;
  }

  private async connectViaChromeExtension(
    apiPath: string,
    token: string,
  ): Promise<void> {
    try {
      // Send message to background script to establish WebSocket connection
      const response = await chrome.runtime.sendMessage({
        type: 'WEBSOCKET_CONNECT',
        config: {
          url: `wss://${apiPath}/wss-api`,
          token: token,
        },
      });

      if (response.success) {
        // Store connection ID for sending messages
        this.connectionId = response.connectionId;

        // Create a port for receiving messages from background script
        this.chromePort = chrome.runtime.connect({ name: 'websocket-port' });

        this.chromePort.onMessage.addListener((message) => {
          if (this.messageHandler && message.type === 'WEBSOCKET_MESSAGE') {
            try {
              const data = JSON.parse(message.data);

              // If streaming content is a string, wrap it in proper structure
              if (
                data.type === 'streaming' &&
                typeof data.content === 'string'
              ) {
                data.content = {
                  type: 'text',
                  text: data.content,
                };
              }

              this.messageHandler(data);
            } catch (error) {
              console.error('Failed to parse message:', error);
              this.messageHandler(message.data);
            }
          }
        });

        this.chromePort.onDisconnect.addListener(() => {
          console.info('Chrome extension WebSocket disconnected');
          this.chromePort = null;
          this.scheduleReconnect();
        });

        console.info('Chrome extension WebSocket connected');
        this.reconnectAttempts = 0;
      } else {
        throw new Error(
          'Failed to establish WebSocket connection via Chrome extension',
        );
      }
    } catch (error) {
      console.error(
        '[WebSocketService] Chrome extension connection error:',
        error,
      );
      throw error;
    }
  }

  private setupWebSocketEventHandlers(): void {
    if (!this.ws) {
      return;
    }

    let opened = false;

    this.ws.onopen = () => {
      console.info('WebSocket connected');
      opened = true;
      this.reconnectAttempts = 0;
    };

    this.ws.onclose = () => {
      console.info('WebSocket disconnected');
      // Handshake rejected and no way to mint a fresh cookie: session is dead
      if (!opened && this.refreshUnauthorized) {
        console.error('[WebSocketService] Session expired, sign in required');
        this.sessionExpired = true;
        return;
      }
      this.scheduleReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    this.ws.onmessage = (event) => {
      if (this.messageHandler) {
        try {
          const data = JSON.parse(event.data);

          // If streaming content is a string, wrap it in proper structure
          if (data.type === 'streaming' && typeof data.content === 'string') {
            data.content = {
              type: 'text',
              text: data.content,
            };
          }

          this.messageHandler(data);
        } catch (error) {
          console.error('Failed to parse message:', error);
          this.messageHandler(event.data);
        }
      }
    };
  }

  public setMessageHandler(handler: (message: unknown) => void): void {
    this.messageHandler = handler;
  }

  private scheduleReconnect(): void {
    if (this.intentionalClose || this.sessionExpired || this.reconnectTimer) {
      return;
    }

    // Exponential back-off, capped. Keeps retrying until it succeeds.
    const delay = Math.min(
      this.reconnectTimeout * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectTimeout,
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private registerRecoveryListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }
    // Reconnect as soon as the user is back, instead of waiting out back-off
    window.addEventListener('online', this.handleRecovery);
    document.addEventListener('visibilitychange', this.handleRecovery);
  }

  private handleRecovery = (): void => {
    if (document.visibilityState !== 'visible') {
      return;
    }
    if (this.intentionalClose || this.sessionExpired || this.connectPromise) {
      return;
    }
    if (
      this.ws?.readyState === WebSocket.OPEN ||
      this.ws?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.connect();
  };

  public disconnect(): void {
    this.intentionalClose = true;
    this.clearReconnectTimer();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    if (this.chromePort) {
      this.chromePort.disconnect();
      this.chromePort = null;
    }
  }

  public sendMessage(message: unknown): void {
    if (isExtension && this.connectionId) {
      // Send message through background script
      chrome.runtime.sendMessage({
        type: 'WEBSOCKET_SEND',
        config: {
          connectionId: this.connectionId,
          data: JSON.stringify(message),
        },
      });
    } else if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
    }
  }

  public getStatus():
    | 'CONNECTING'
    | 'OPEN'
    | 'CLOSING'
    | 'CLOSED'
    | 'NOT_INITIALIZED' {
    if (isExtension) {
      if (this.chromePort) {
        return 'OPEN';
      }
      return 'NOT_INITIALIZED';
    }

    if (!this.ws) {
      return 'NOT_INITIALIZED';
    }
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'OPEN';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'NOT_INITIALIZED';
    }
  }

  private connectToLocal(): boolean {
    return window.location.hostname === 'localhost';
  }
}
